import { isElectron } from '@/renderer/util';
import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types/pagination';
import { supabase } from './supabase';

export interface EbmItemClassification {
  item_cls_cd: string;
  item_cls_nm: string;
}

const PAGE_SIZE = 20;

// Backs the searchable classification-code picker on the fiscal setup
// pages — reads the local cache table populated by syncItemClasses()
// below, never a live per-keystroke call to RRA (selectItemsClass has no
// search parameter; it's a bulk pull meant to be cached and searched
// client-side).
export async function fetchItemClassifications({
  search,
  page = 1,
  pageSize = PAGE_SIZE,
}: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<PaginatedResponse<EbmItemClassification>>> {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('ebm_item_classifications')
      .select('item_cls_cd, item_cls_nm', { count: 'exact' })
      .eq('use_yn', 'Y')
      .order('item_cls_nm', { ascending: true })
      .range(from, to);

    if (search) {
      query = query.or(
        `item_cls_nm.ilike.%${search}%,item_cls_cd.ilike.%${search}%`,
      );
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: (data || []) as EbmItemClassification[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch item classifications',
      data: undefined,
    };
  }
}

export interface SyncItemClassesResult {
  syncedCount: number;
}

// Pulls RRA's real item classification catalog (via the local VSDC) and
// caches it in ebm_item_classifications — never fabricates/randomly
// generates codes, since these are compliance-critical values only RRA
// itself can issue.
export async function syncItemClasses(
  outletId: string,
): Promise<ApiResponse<SyncItemClassesResult>> {
  if (!isElectron()) {
    throw new Error('Item class sync is only available in the desktop app.');
  }
  const result = await window.electron.ipcRenderer.invoke(
    'ebm:syncItemClasses',
    { outletId },
  );
  return result as ApiResponse<SyncItemClassesResult>;
}
